import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ChevronRight } from "lucide-react";
import { CONCEPTS, BRAND_COLORS, formatPrice } from "@/lib/menuData";

const BURGER_CATEGORIES = ["lunch_burger", "hamburger", "cheeseburger"];

// Map an item to its on-page section anchor id.
// All burger categories share a single "Island Burgers" section under the lunch tab.
export function getSectionIdForItem(item) {
  const cat = (item.category || "").toLowerCase();
  if (BURGER_CATEGORIES.indexOf(cat) !== -1) return "section-burgers";
  return `section-${cat}`;
}

// Map an item to the concept tab it lives under.
// Burgers (any category) live under the Lunch tab on the customer page.
export function getConceptForItem(item) {
  const cat = (item.category || "").toLowerCase();
  if (BURGER_CATEGORIES.indexOf(cat) !== -1) return "lunch";
  const c = (item.concept || "").toLowerCase();
  if (CONCEPTS.find((x) => x.id === c)) return c;
  return "chicken";
}

function conceptMeta(conceptId) {
  return CONCEPTS.find((c) => c.id === conceptId) || CONCEPTS[0];
}

function highlightMatch(text, query) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        className="rounded px-0.5"
        style={{ backgroundColor: BRAND_COLORS.sunsetOrange + "33", color: BRAND_COLORS.darkSlate }}
      >
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function filterItems(items, query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return items
    .filter((it) => it.available !== false && it.published !== false)
    .filter((it) => {
      const name = (it.name || "").toLowerCase();
      const desc = (it.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    })
    .slice(0, 12);
}

/**
 * MenuSearch — full-screen overlay search for the customer order page.
 * - Desktop trigger lives in the header (top right).
 * - Mobile trigger is a floating button above the cart button.
 * Renders nothing until `open` is true.
 */
export default function MenuSearch({ open, onClose, menuItems, onSelectItem }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  // Reset query whenever the overlay opens
  useEffect(() => {
    if (open) {
      setQuery("");
      // Focus the input on next tick
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Escape key closes
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Lock body scroll while overlay is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const results = useMemo(() => filterItems(menuItems || [], query), [menuItems, query]);

  if (!open) return null;

  const handleSelect = (item) => {
    onSelectItem(item);
    onClose();
  };

  return (
    <div
      data-testid="menu-search-overlay"
      className="fixed inset-0 z-[60] flex flex-col"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.55)" }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-3xl mx-auto mt-0 sm:mt-12 rounded-none sm:rounded-2xl shadow-2xl flex flex-col h-full sm:h-auto sm:max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: "#E5E7EB" }}>
          <Search className="h-5 w-5 flex-shrink-0" style={{ color: BRAND_COLORS.darkSlate + "99" }} />
          <input
            ref={inputRef}
            data-testid="menu-search-input"
            type="text"
            inputMode="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search burgers, wings, pizza, sides..."
            className="flex-1 outline-none text-base bg-transparent"
            style={{ color: BRAND_COLORS.darkSlate, fontFamily: "Manrope" }}
          />
          <button
            data-testid="menu-search-close"
            onClick={onClose}
            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
            aria-label="Close search"
          >
            <X className="h-5 w-5" style={{ color: BRAND_COLORS.darkSlate + "99" }} />
          </button>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto" data-testid="menu-search-results">
          {query.trim() === "" && (
            <div className="p-8 text-center">
              <Search className="h-10 w-10 mx-auto mb-3" style={{ color: BRAND_COLORS.darkSlate + "33" }} />
              <p className="text-sm font-medium" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                Start typing to find any item on the menu
              </p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
                Wings, burgers, pizza, sides, sandwiches &mdash; it&apos;s all searchable
              </p>
            </div>
          )}

          {query.trim() !== "" && results.length === 0 && (
            <div className="p-8 text-center" data-testid="menu-search-empty">
              <p className="text-sm font-medium" style={{ color: BRAND_COLORS.darkSlate }}>
                No matches for &ldquo;{query}&rdquo;
              </p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
                Try a different word &mdash; maybe &ldquo;burger&rdquo;, &ldquo;wings&rdquo; or &ldquo;pizza&rdquo;
              </p>
            </div>
          )}

          {results.length > 0 && (
            <ul className="py-1">
              {results.map((item) => {
                const conceptId = getConceptForItem(item);
                const meta = conceptMeta(conceptId);
                return (
                  <li key={item.id}>
                    <button
                      data-testid={`menu-search-result-${item.id}`}
                      onClick={() => handleSelect(item)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                    >
                      {item.image ? (
                        <img
                          src={item.image}
                          alt=""
                          className="h-12 w-12 rounded-lg object-cover flex-shrink-0 bg-slate-100"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="h-12 w-12 rounded-lg flex-shrink-0 flex items-center justify-center"
                          style={{ backgroundColor: meta.color + "15" }}
                        >
                          <Search className="h-4 w-4" style={{ color: meta.color }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className="font-semibold text-sm leading-snug"
                          style={{ color: BRAND_COLORS.darkSlate, fontFamily: "Manrope" }}
                        >
                          {highlightMatch(`${item.name}${item.size ? ` (${item.size})` : ""}`, query)}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span
                            className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: meta.color + "20", color: meta.color }}
                          >
                            {meta.shortName}
                          </span>
                          {item.description ? (
                            <span
                              className="text-xs truncate"
                              style={{ color: BRAND_COLORS.darkSlate + "99" }}
                            >
                              {highlightMatch(item.description, query)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <span
                        className="font-bold text-sm flex-shrink-0"
                        style={{ color: BRAND_COLORS.sunsetOrange, fontFamily: "Manrope" }}
                      >
                        {formatPrice(item.price)}
                      </span>
                      <ChevronRight
                        className="h-4 w-4 flex-shrink-0"
                        style={{ color: BRAND_COLORS.darkSlate + "55" }}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * MenuSearchTrigger — header pill input (desktop / tablet, hidden on mobile).
 * Looks like a search box but functions as a trigger that opens the overlay.
 */
export function MenuSearchTrigger({ onOpen }) {
  return (
    <button
      data-testid="menu-search-trigger-desktop"
      onClick={onOpen}
      className="hidden md:flex items-center gap-2 h-9 px-3 rounded-full border border-white/30 hover:border-white/60 hover:bg-white/10 transition-all min-w-[200px]"
      aria-label="Search menu"
    >
      <Search className="h-4 w-4 text-white/80" />
      <span className="text-sm text-white/80 flex-1 text-left" style={{ fontFamily: "Manrope" }}>
        Search menu
      </span>
      <kbd
        className="hidden lg:inline text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/30 text-white/60"
      >
        /
      </kbd>
    </button>
  );
}

// MenuSearchFab removed — mobile users now access search via the desktop pill
// in the header. Keeping the export point clean and avoiding clutter at the
// bottom of the screen on mobile.
